import { BadgeCheck } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { LiquidPageHeader } from "@/components/liquid/LiquidPageHeader";
import { SplitText } from "@/components/liquid/SplitText";
import { ZoomParallax } from "@/components/liquid/ZoomParallax";
import { VelocityMarquee } from "@/components/liquid/VelocityMarquee";
import { VideoBackdrop } from "@/components/liquid/VideoBackdrop";
import { ParallaxY } from "@/components/liquid/ParallaxY";
import { pageVideo } from "@/lib/videos";
import { APPROACH } from "@/lib/content";
import { useContent } from "@/context/ContentContext";
import { useBooking } from "@/context/BookingContext";

export default function DoctorsLiquid() {
  const { content } = useContent();
  const { book } = useBooking();
  const doctors = content?.doctors || [];

  return (
    <div className="relative z-[1] px-4 pb-24 pt-36 sm:px-8 sm:pt-44 lg:px-16" data-testid="doctors-liquid">
      <VideoBackdrop src={pageVideo(content?.settings, "doctors")} testid="doctors-video-bg" />
      <div className="mx-auto max-w-6xl">
        <LiquidPageHeader
          index="02"
          eyebrow="Our Physicians"
          title="Masters of the Craft"
          subtitle="Board-certified expertise with an artist's restraint — results that look like you, only luminous."
          testid="doctors-title"
        />

        <div className="mt-16 space-y-24" data-testid="doctors-list">
          {doctors.map((d, i) => (
            <div key={d.id} className="grid items-center gap-12 lg:grid-cols-12" data-testid={`doctor-profile-${d.id}`}>
              <div className={i % 2 === 1 ? "lg:order-2 lg:col-span-5" : "lg:col-span-5"}>
                <Reveal>
                  <ParallaxY speed={i % 2 === 1 ? -0.6 : 0.6}>
                    <ZoomParallax src={d.image} alt={`Portrait of ${d.name}`} className="h-[60vh] rounded-[4px] shadow-[0_40px_90px_rgba(26,26,26,0.15)]" />
                  </ParallaxY>
                </Reveal>
              </div>
              <div className={i % 2 === 1 ? "lg:order-1 lg:col-span-7" : "lg:col-span-7"}>
                <Reveal><p className="eyebrow">{d.role}</p></Reveal>
                <Reveal delay={0.1}>
                  <h2 className="mt-4 font-display text-5xl font-normal leading-[1.02] text-[#1A1A1A] sm:text-6xl">
                    <SplitText text={d.name} inView stagger={0.025} />
                  </h2>
                </Reveal>
                <Reveal delay={0.2}>
                  <p className="mt-3 text-[11px] font-medium uppercase tracking-[0.25em] text-[#1A1A1A]/45">{d.experience} of clinical excellence</p>
                </Reveal>
                <Reveal delay={0.25}>
                  <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-[#5A504B]">{d.bio}</p>
                </Reveal>
                <Reveal delay={0.35}>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {(d.specializations || []).map((sp) => (
                      <span key={sp} className="border border-[#1A1A1A]/15 px-3.5 py-1.5 text-[11px] font-medium uppercase tracking-[0.12em] text-[#1A1A1A]/60">
                        {sp}
                      </span>
                    ))}
                  </div>
                </Reveal>
                <Reveal delay={0.45}>
                  <ul className="mt-6 space-y-2">
                    {(d.certifications || []).map((c) => (
                      <li key={c} className="flex items-center gap-2 text-sm font-light text-[#5A504B]">
                        <BadgeCheck className="h-4 w-4 shrink-0 text-[#B38B2D]" aria-hidden="true" /> {c}
                      </li>
                    ))}
                  </ul>
                </Reveal>
                <Reveal delay={0.55}>
                  <button onClick={() => book()} data-testid={`doctor-book-${d.id}`} className="btn-gold mt-8 px-8 py-3.5 text-sm">
                    Book with {d.name}
                  </button>
                </Reveal>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-24">
        <VelocityMarquee items={["Precision", "Restraint", "Artistry", "Care"]} />
      </div>

      {/* APPROACH */}
      <section className="mx-auto mt-24 max-w-6xl" data-testid="approach-section">
        <Reveal><p className="eyebrow">The Royal Skin Approach</p></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-3 font-display text-4xl font-normal text-[#1A1A1A] sm:text-5xl">How We Practice</h2>
        </Reveal>
        <div className="mt-12 grid gap-px overflow-hidden rounded-[4px] bg-[#1A1A1A]/10 lg:grid-cols-3">
          {APPROACH.map((a, i) => (
            <Reveal key={a.n} delay={i * 0.1} className="bg-[#FAF8F5]">
              <div className="h-full p-10" data-testid={`approach-${a.n}`}>
                <p className="font-display text-5xl italic text-[#D4AF37]/50">{a.n}</p>
                <h3 className="mt-5 font-display text-2xl font-normal text-[#1A1A1A]">{a.title}</h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-[#5A504B]">{a.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
